<template>
  <div>
    <loading-progress-bar
      v-if="loading"
      :loading="loading"
      color="secondary"
    ></loading-progress-bar>

    <v-layout row wrap v-if="!loading && ecConfig">
      <v-flex xs10>
        <vue-echart
          :white-mode="whiteMode"
          :config="ecConfig"
          :height="height"
        ></vue-echart>
      </v-flex>

      <v-flex xs20>
        <v-layout row wrap>
          <v-flex xs20 >
            <value-box :white-mode="whiteMode" :value="PMV" :title="'PMV'|translate"></value-box>
            <value-box :white-mode="whiteMode" :value="displayValues.dryBulbTemp" unit="°C" :title="'dry-Bulb Temperature'|translate"></value-box>
            <value-box :white-mode="whiteMode" :value="displayValues.relativeHumidity" unit="%" :title="'relative Humidity'|translate"></value-box>
            <value-box v-if="!hideParameters" :white-mode="whiteMode" :value="displayValues.airSpeed" unit="m/s" :title="'air Speed'|translate"></value-box>
            <value-box v-if="!hideParameters" :white-mode="whiteMode" :value="displayValues.metabolicRate" unit="met" :title="'metabolic Rate'|translate"></value-box>
            <value-box v-if="!hideParameters" :white-mode="whiteMode" :value="displayValues.clothingLevel" unit="clo" :title="'clothing Level'|translate"></value-box>
          </v-flex>
        </v-layout>
      </v-flex>

    </v-layout>
  </div>
</template>

<script>
import LoadingProgressBar from "./../LoadingProgressBar";
import VueEchart from "@/components/VueEchart";
import psychrolib from "./libs/psychrolib.js";
import pmvlib from "./libs/pmvlib.js";
import ValueBox from "./../ValueBox";
import moment from "moment";
import {callAPI} from '@/helper'
import data3Processor from "../mixins/data3Processor";



export default {
  props: {
    title: { type: String, default: "Thermal Comfort" },
    dateRange: { type: Object, default: null },
    whiteMode: { type: Boolean, default: false },
    height: { type: Number, default: 650 },
    dryBulbTemp: { type: Number, default: 25 },
    // radiantTemp: { type: Number, default: 25 },
    relativeHumidity: { type: Number, default: 50 }, 
    airSpeed: { type: Number, default: 0.1 },
    metabolicRate: { type: Number, default: 1.2 }, 
    clothingLevel: { type: Number, default: 0.61 },
    equipment: {type:String, default: null},
    data3query: {type: Object, default: null},
    parameters: {type: Object, default: null},
    hideParameters: {type: Boolean, default: false},
    hideTempScale: {type: Boolean, default: false},
    enableSolar: {type: Boolean, default: false},
    solarAltitude: {type: Number, default: 0},
    solarAzimuth: {type: Number, default: 0},
    solarIrradiance: {type: Number, default: 0},
    solarTransmittance: {type: Number, default: 0},
    f_svv: {type: Number, default: 0.2},
    f_bes: {type: Number, default: 0.5},
    asw: {type: Number, default: 0.7}
  },
  components: { LoadingProgressBar, VueEchart, ValueBox },
  mixins: [data3Processor],
  computed: {},
  name: "ThermalComfortWidget",
  watch: {
      dateRange () {
          if(this.data3query) {
              this.refresh();
          }
      },
      equipment () {
          if(this.data3query){
              this.refresh()
          }
      },
      parameters:{
          handler() {
              if(this.data3query){
                  this.refresh()
              }
          },
          deep: true,
          immediate: true,
      },
  },
  data: function() {
    return {
      loading: true,
      timeout: null,
      ecConfig: null,
      psychrolib: null,
      displayValues:{},
        reloadInc:0,
      PMV: 0
    };
  },
  mounted() {
    this.refresh();
  },
  beforeMount() {
    psychrolib.SetUnitSystem(psychrolib.SI);
    this.psychrolib = require("./libs/psychrolib.js");
    this.pmvlib = require("./libs/pmvlib.js");
  },
  methods: {
    debounce(callback, wait) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => callback(), wait);
    },
    refresh() {
      this.debounce(this.getData, 500);
    },

    range(start, stop, step) {
      step = step || 1;
      var arr = [];
      for (var i = start; i < stop; i += step) {
        arr.push(i);
      }
      return arr;
    },

    getHumRatio(rh_decimal) {
      let pressure = 101325;
      let hr_array = [];
      let t_array = this.range(10, 45, 1);
      for (const t of t_array) {
        let hr = psychrolib.GetHumRatioFromRelHum(t, rh_decimal, pressure);
        hr_array.push([t, hr]);
      }
      return hr_array;
    },

    getTwestbulb(twb) {
      let pressure = 101325;
      let t_array = this.range(10, 45, 1);
      for (const t of t_array) {
        if (twb <= t) {
          let hr = psychrolib.GetHumRatioFromTWetBulb(t, twb, pressure);
          hr_array.push([t, hr]);
        }
      }
      return hr_array;
    },

    // psychrolib uses decimal point, but we uses percentage. Need to convert
    convertToDecimal(rh_array){
      let rhDecimal_array = [];
      for (const rh of rh_array) {
        let rh_decimal = rh/100;
        rhDecimal_array.push(rh_decimal);
      }
      return rhDecimal_array;
    },
    
    async getData() {
        let pressure = 101325;
        let latestData = {}
        let histData = {}
        this.reloadInc=this.reloadInc+1
        let currentInc=this.reloadInc
        if (this.data3query) {
            let payloadArray = JSON.parse(JSON.stringify(this.data3query['data-payload']))
            payloadArray = payloadArray.filter(x => x.filters && (x.filters.sensor_name || x.filters.equipment_name))
            for (let payload of payloadArray) {
                payload.parameters = this.parameters
                this.processDateRange(payload,this.dateRange)
                if (!payload.filters.facility_name && this.$store.state.pointsListStore.pointsList.facility_name) {
                    payload.filters.facility_name = this.$store.state.pointsListStore.pointsList.facility_name
                }
                if (this.equipment) {
                    this.processEquipment(payload, this.equipment)
                }
                if (payload.onOnly) {
                    if (!payload.pipeline) {
                        payload.pipeline = []
                    }
                    payload.pipeline.unshift(
                        {
                            "type": "filter",
                            "filter_op": "eq",
                            "filter_by": "* - on",
                            "filter_value": 1,
                        }
                    )
                }
            }
            let results = await Promise.all(payloadArray.map(p => callAPI('data3/', p)))
            if (currentInc!==this.reloadInc){
                return
            }
            for (let result of results) {
                for (let seriesIndex in result.data) {
                    if (seriesIndex === "timestamp") {
                        continue
                    }
                    latestData[seriesIndex]=result.data[seriesIndex].reduce((acc, curr) => curr !== null ? curr : acc)
                    histData[seriesIndex]=result.data[seriesIndex]
                }
            }
        }

      // Get PMV
      let tdb = latestData["dryBulbTemp"] ? latestData["dryBulbTemp"] : this.dryBulbTemp;
      let tr = tdb  // latestData["radiantTemp"] ? latestData["radiantTemp"] : this.radiantTemp;
      let rh = latestData["relativeHumidity"] ? latestData["relativeHumidity"] : this.relativeHumidity;
      let v = latestData["airSpeed"] ? latestData["airSpeed"] : this.airSpeed;
      let met = latestData["metabolicRate"] ? latestData["metabolicRate"] : this.metabolicRate;
      let clo = latestData["clothingLevel"] ? latestData["clothingLevel"] : this.clothingLevel;
      let wme = 0;
      let body_position = "standing";
      this.displayValues={
          dryBulbTemp:tdb,
          radiantTemp:tr,
          relativeHumidity:rh,
          airSpeed:v,
          metabolicRate:met,
          clothingLevel:clo
      }

      /*enableSolar: {type: Boolean, default: false},
    solarAltitude: {type: Number, default: 0},
    solarAzimuth: {type: Number, default: 0},
    solarIrradiance: {type: Number, default: 0},
    solarTransmittance: {type: Number, default: 0},
    f_svv: {type: Number, default: 0.2},
    f_bes: {type: Number, default: 0.5},
    asw: {type: Number, default: 0.7} */
      if (this.enableSolar) {
        let solarAltitude = latestData["solarAltitude"]? latestData["solarAltitude"]: this.solarAltitude;
        let solarAzimuth = latestData["solarAzimuth"]? latestData["solarAzimuth"]: this.solarAzimuth;
        let solarIrradiance = latestData["solarIrradiance"]? latestData["solarIrradiance"]: this.solarIrradiance;
        let solarTransmittance = latestData["solarTransmittance"]? latestData["solarTransmittance"]: this.solarTransmittance;
        let f_svv = latestData["f_svv"]? latestData["f_svv"]: this.f_svv;
        let f_bes = latestData["f_bes"]? latestData["f_bes"]: this.f_bes;
        let asw = latestData["asw"]? latestData["asw"]: this.asw;
        let adjustment = this.pmvlib.solar_gain(
            solarAltitude,
            solarAzimuth,
            solarIrradiance,
            solarTransmittance,
            f_svv,
            f_bes,
            asw
        )
        tdb = tdb + adjustment["delta_mrt"];
      }


      let pmv_result = pmvlib.pmv_withCE(
        tdb,
        tr,
        v,
        rh,
        met,
        clo,
        wme,
        body_position
      );

      this.PMV = pmv_result;
      let rh_decimal = rh / 100;
      let hr_result = psychrolib.GetHumRatioFromRelHum(tdb, rh_decimal, pressure).toFixed(3);
      let dataset = [[tdb, hr_result, pmv_result]];

      let t_1 = pmvlib.comfortRange(v, 100, met, clo, wme, body_position, -0.5);
      let t_2 = pmvlib.comfortRange(v, 100, met, clo, wme, body_position, 0.5);
      let t_3 = pmvlib.comfortRange(v, 0, met, clo, wme, body_position, -0.5);
      let t_4 = pmvlib.comfortRange(v, 0, met, clo, wme, body_position, 0.5);

      let hr_1 = psychrolib.GetHumRatioFromRelHum(t_1, 1, pressure);
      let hr_2 = psychrolib.GetHumRatioFromRelHum(t_2, 1, pressure);
      let hr_3 = psychrolib.GetHumRatioFromRelHum(t_3, 0, pressure);
      let hr_4 = psychrolib.GetHumRatioFromRelHum(t_4, 0, pressure);

      let point_1 = [t_1, hr_1];
      let point_2 = [t_2, hr_2];
      let point_3 = [t_3, hr_3];
      let point_4 = [t_4, hr_4];

      let comfort_range = [point_1, point_2, point_4, point_3];

      
      // Get history point: Input[tdb, rh], Out[tdb, hr]
      let histPoints = [];
      let histTdb = histData["dryBulbTemp"] ? histData["dryBulbTemp"] : [this.dryBulbTemp];
      let histRh = histData["relativeHumidity"] ? histData["relativeHumidity"] : [this.relativeHumidity];

      let histRh_decimal = this.convertToDecimal(histRh);

      for (let i=0, len=histTdb.length; i<len; i++) {
          let hr = psychrolib.GetHumRatioFromRelHum(histTdb[i], histRh_decimal[i], pressure);
          histPoints.push([histTdb[i], hr]);
      }

      // chartStyle
      let relhum_lineStyle = {
        color: "rgb(229, 229, 255)",
        width: 1.0,
        type: "dashed",
      };


      this.loading = true;
      this.ecConfig = {
        animation: false,
        title: {
          text: this.title,
          subtext: 'ASHRAE 55-2020',
          left: "center",
        },
        grid: {
          top: 80,
          left: 150,
          right: 150,
          bottom: 80,
        },
        xAxis: {
          name: "   Dry-bulb\nTemperature\n      (°C)",
          min: 10,
          max: 40,
          splitLine: {
            lineStyle: {
              width: 0.1,
            },
          },
          minorTick: {
            show: true,
          },
        },
        yAxis: {
          name: "Humidity Ratio\n(kg/kg)",
          min: 0.0,
          max: 0.04,
          splitLine: {
            lineStyle: {
              width: 0.1,
            },
          },
          minorTick: {
            show: true,
          },
          axisLabel: {
            formatter: function(value, index) {
              return value.toFixed(3);
            },
          },
        },
        visualMap: !this.hideTempScale ? {
          type: 'continuous',
          orient: 'horizontal',
          left: 'center',
          itemWidth: 15,
          itemHeight: 600,
          min: 10,
          max: 40,
          precision: 1,
          text: ['Very Hot', 'Very Cold'],
          seriesIndex: 1,
          dimension: 0,
          textStyle: {
              color: '#eee'
          },
          calculable: true,
          inRange: {
            color: ['#3398ff', '#99ff66' , '#FD665F']
          },
          outOfRange: {
            color: 'rgba(255,255,255,0.5)'
          },
        } : {show: false},
        series: [
          {
            name: "Thermal Comfort",
            type: "effectScatter",
            symbolSize: 15,
            data: dataset,
            zlevel: 10,
            color: '#ffd866'
          },
          {
            type: "scatter",
            symbolSize: 8,
            data: histPoints,
            zlevel: 9,
          },
          {
            type: 'custom',
            renderItem: function (params, api) {
              if (params.context.rendered) {
                return;
              }
              params.context.rendered = true;
              let points = [];
              for (let i = 0; i < comfort_range.length; i++) {
                points.push(api.coord(comfort_range[i]))
              }
              return {
                type: 'polygon',
                transition: ['shape'],
                shape: {
                  points: points
                },
                style: api.style({
                  fill: 'rgba(204, 255, 179, 0.2)'
                })
              };
            },
            clip: true,
            data: comfort_range
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.1),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.2),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.3),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.4),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.5),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.6),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.7),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.8),
          },
          {
            type: "line",
            lineStyle: {
              normal: relhum_lineStyle,
            },
            showSymbol: false,
            data: this.getHumRatio(0.9),
          },
          {
            type: "line",
            lineStyle: {
              normal: {
                color: "rgb(229, 229, 255)",
                width: 1.8,
              },
            },
            showSymbol: false,
            data: this.getHumRatio(1),
          }
        ],
      };

      this.loading = false;
    },
  },
};
</script>

<style scoped></style>
